import type { AppProps } from 'next/app'
import localFont from 'next/font/local'
import '@/assets/styles/global.scss'
import '@/locales/i18n'
// import I18nProvider from '@/providers/i18n.provider'
import { wrapper } from '@/redux/store'
import { FC, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
// import ThemeProvider from '@/providers/theme.provider'
import { QueryClientProvider } from 'react-query'
import queryClient from '@/utils/configs/react-query.config'
import { persistStore } from 'redux-persist'
import Meta from '@/components/meta/Meta'
import dynamic from 'next/dynamic'

const OffBanner = dynamic(() => import('@/components/ui/OffBanner'), {
  ssr: false,
})

const I18nProvider = dynamic(() => import('@/providers/i18n.provider'), {
  ssr: true,
})

const ThemeProvider = dynamic(() => import('@/providers/theme.provider'), {
  ssr: true,
})

type TProps = AppProps & {}

const manropeFont = localFont({
  preload: true,
  display: 'swap',
  src: [
    {
      path: '../assets/fonts/manrope_medium.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: '../assets/fonts/manrope_semibold.woff2',
      weight: '600',
      style: 'normal',
    },
  ],
  fallback: ['system-ui', 'arial'],
  adjustFontFallback: 'Arial',
})

const NextApp: FC<TProps> = ({ Component, ...rest }) => {
  const { store, props } = wrapper.useWrappedStore(rest)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      persistStore(store)
    }
  }, [store])

  const [startupQueryClient] = useState(queryClient)

  const [isOpenBanner, setIsOpenBanner] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsOpenBanner(true)
    }, 10000)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <QueryClientProvider client={startupQueryClient}>
      <Provider store={store}>
        <ThemeProvider>
          <I18nProvider>
            <div className={manropeFont.className}>
              <Component {...props.pageProps} />
              <OffBanner isOpen={isOpenBanner} onClose={() => setIsOpenBanner(false)} />
            </div>
          </I18nProvider>
        </ThemeProvider>
        <Meta />
      </Provider>
    </QueryClientProvider>
  )
}

export default NextApp
